import imageCompression from 'browser-image-compression';

import { isBlob } from '../../../utils/capture';

/*
  From idology:
  - Put the image format at 90% quality if possible within the sizing constraints.
  - Compress both images, front and back; each image must not exceed 5 megabytes.
*/

const JPEG = 'image/jpeg';
const MAX_SIZE_MB = 4;
export const COMPRESS_EXTRA_MAX_SIZE_MB = 0.3;
const SLOW_CONNECTION_MAX_SIZE_MB = 1;
// WebKit (all of iOS) and Firefox never expose navigator.connection, so the slow-link check can't
// protect them: hold the same 1 MB ceiling, but try it at full resolution first — a light document
// fits without shrinking, and these devices can afford the retry when a heavy one doesn't.
const UNKNOWN_CONNECTION_MAX_SIZE_MB = SLOW_CONNECTION_MAX_SIZE_MB;
const SLOW_EFFECTIVE_TYPES = ['slow-2g', '2g', '3g'];
// hasBadConnectivity only turns on after an upload has already failed, so the budget is sized from
// the connection up front. navigator.connection only estimates *downlink* and mobile uplink runs
// well under it, so assume a fraction of it and check the budget against the timeout.
const UPLOAD_TIMEOUT_MS = 61_000; // DOC_UPLOAD_TIMEOUT_MS; unimportable, this file is copied verbatim into idv
const UPLOAD_HEADROOM = 0.6; // TLS, latency and server time come out of the same budget
const ASSUMED_UPLINK_FRACTION = 0.25;

const canUploadWithin = (sizeMB: number, downlinkMbps: number): boolean => {
  const uplinkMbps = downlinkMbps * ASSUMED_UPLINK_FRACTION;
  return ((sizeMB * 8) / uplinkMbps) * 1000 <= UPLOAD_TIMEOUT_MS * UPLOAD_HEADROOM;
};

/*
  keepResolution stops each pass shrinking the image 5% alongside its quality drop — without it a
  sharper source lands with fewer pixels than a mediocre one and fails Incode's sharpness check.
  The quality walk bottoms out near q0.6 (10 passes x 0.95) though, so the deliberately small
  budgets skip straight to downscaling instead of burning a pipeline on a pass that cannot reach them.
*/
type CompressionBudget = { maxSizeMB: number; keepResolution: boolean };

const getBudget = (extraCompress?: boolean): CompressionBudget => {
  if (extraCompress) {
    return { maxSizeMB: COMPRESS_EXTRA_MAX_SIZE_MB, keepResolution: false };
  }

  // navigator.connection is not in the DOM lib.
  type NetworkInformation = { effectiveType?: string; saveData?: boolean; downlink?: number };
  const connection =
    typeof navigator === 'undefined'
      ? undefined
      : (navigator as Navigator & { connection?: NetworkInformation }).connection;
  if (!connection) {
    return { maxSizeMB: UNKNOWN_CONNECTION_MAX_SIZE_MB, keepResolution: true };
  }

  const isSlow =
    SLOW_EFFECTIVE_TYPES.includes(String(connection.effectiveType)) ||
    connection.saveData === true ||
    (typeof connection.downlink === 'number' && !canUploadWithin(MAX_SIZE_MB, connection.downlink));
  return isSlow
    ? { maxSizeMB: SLOW_CONNECTION_MAX_SIZE_MB, keepResolution: false }
    : { maxSizeMB: MAX_SIZE_MB, keepResolution: true };
};

// Input: Non-HEIC Image File. Output: Compressed JPEG Image File.
const compressImage = async (fileOrBlob: File | Blob, extraCompress?: boolean): Promise<File | undefined> => {
  const { maxSizeMB, keepResolution } = getBudget(extraCompress);
  const options = {
    maxSizeMB,
    alwaysKeepResolution: keepResolution,
    useWebWorker: true,
    fileType: JPEG,
  };

  const file = isBlob(fileOrBlob)
    ? new File(Array.isArray(fileOrBlob) ? fileOrBlob : [fileOrBlob], 'file-to-compress', {
        type: JPEG,
        lastModified: Date.now(),
      })
    : fileOrBlob;

  const compressed = await imageCompression(file, options);

  // The quality walk gives up after a fixed number of passes, so it can still come back over
  // budget. A downscaled document beats one that times out or the API refuses.
  if (keepResolution && compressed.size > maxSizeMB * 1024 * 1024) {
    return imageCompression(file, { ...options, alwaysKeepResolution: false });
  }

  return compressed;
};

export default compressImage;
